import { useEffect, useRef, useState } from 'react';
import type { InfoPageId } from '../../content/infoPages';
import { navigate } from '../../hooks/useHashRoute';

interface SiteMenuProps {
  currentPage?: InfoPageId;
}

const MENU_ITEMS: Array<{ page: InfoPageId; label: string; description: string }> = [
  { page: 'how-it-works', label: 'How It Works', description: 'The radar pipeline explained' },
  { page: 'guides', label: 'Guides', description: 'Read the tables, cards, and charts' },
  { page: 'about', label: 'About', description: 'What Pilum is and how it is built' },
  { page: 'disclaimer', label: 'Disclaimer', description: 'Research tool, not financial advice' },
  { page: 'terms', label: 'Terms & Conditions', description: 'Usage terms and warranty limits' },
  { page: 'privacy', label: 'Privacy Policy', description: 'Local-only data and public feeds' },
];

export function SiteMenu({ currentPage }: SiteMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Close whenever the route changes (keyboard navigation through the brand,
  // search, or a menu item) so the dropdown never survives a navigation.
  useEffect(() => {
    const onHashChange = (): void => setOpen(false);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return (
    <div className="site-menu" ref={rootRef}>
      <button
        ref={toggleRef}
        type="button"
        className="site-menu-button"
        aria-expanded={open}
        aria-controls="site-menu-panel"
        aria-label="Open documentation and legal pages"
        onClick={() => setOpen((value) => !value)}
      >
        <span className="site-menu-lines" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
        <span className="site-menu-button-label">Menu</span>
      </button>

      {open && (
        <div id="site-menu-panel" className="site-menu-panel">
          <div className="site-menu-head">
            <strong>Explore Pilum</strong>
            <span>Documentation, guides, and legal information</span>
          </div>
          <nav aria-label="Site pages">
            {MENU_ITEMS.map((item) => (
              <button
                key={item.page}
                type="button"
                className="site-menu-item"
                data-active={currentPage === item.page}
                onClick={() => navigate(`/${item.page}`)}
              >
                <strong>{item.label}</strong>
                <span>{item.description}</span>
              </button>
            ))}
          </nav>
        </div>
      )}
    </div>
  );
}
